import { useEffect, useMemo, useState } from "react";
import { Clock, Users, CalendarDays, CheckCircle2, Loader2, XCircle, ChevronRight, ArrowRight } from "lucide-react";
import { api, fmtCurrency, fmtDateLabel, isoStartDateTime, nextNDates, parseApiError } from "../lib/api";
import ErrorMessage from "./ErrorMessage";
import Calendar from "./Calendar";

function GuestModal({
  open, trip, date, onClose,
  // Step A
  onSubmitForm, formBusy, formError,
  // Step B
  createdReservation, onPayDeposit, payBusy, payError,
}) {
  const [form, setForm] = useState({
    FirstName: "", LastName: "", Email: "", Phone: "", Guests: 2,
  });
  const [copied, setCopied] = useState(false);
  const upd = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));

  if (!open) return null;

  const submit = (e) => { e.preventDefault(); onSubmitForm(form); };
  const code = createdReservation?.confirmationCode;

  const copyCode = async () => {
    if (!code) return;
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch {/* ignore */}
  };

  return (
    <div className="aq-modal-overlay" onClick={onClose} data-testid="booking-modal">
      <div className="aq-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="aq-overline">{createdReservation ? "Reservation Confirmed" : "Confirm Your Trip"}</div>
            <h3 className="aq-h2 text-2xl mt-2" style={{ color: "var(--aq-navy)" }}>
              {trip?.name}
            </h3>
            <p className="text-sm mt-1" style={{ color: "var(--aq-text-soft)" }}>
              {fmtDateLabel(date)} · {trip?.validFromTime?.slice(0, 5)}–{trip?.validToTime?.slice(0, 5)} · {fmtCurrency(trip?.amount)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm" data-testid="booking-modal-close" style={{ color: "var(--aq-text-soft)" }}>Close</button>
        </div>

        {/* ---- Step A: guest form ---- */}
        {!createdReservation && (
          <form onSubmit={submit} data-testid="booking-form">
            <div className="mt-6 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "var(--aq-text-soft)" }}>First name</span>
                <input className="aq-input mt-1" required data-testid="booking-first-name" value={form.FirstName} onChange={upd("FirstName")} />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "var(--aq-text-soft)" }}>Last name</span>
                <input className="aq-input mt-1" required data-testid="booking-last-name" value={form.LastName} onChange={upd("LastName")} />
              </label>
              <label className="block col-span-2">
                <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "var(--aq-text-soft)" }}>Email</span>
                <input type="email" className="aq-input mt-1" required data-testid="booking-email" value={form.Email} onChange={upd("Email")} />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "var(--aq-text-soft)" }}>Phone</span>
                <input className="aq-input mt-1" required data-testid="booking-phone" value={form.Phone} onChange={upd("Phone")} />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "var(--aq-text-soft)" }}>Guests</span>
                <input type="number" min={1} max={4} className="aq-input mt-1" required data-testid="booking-guests" value={form.Guests} onChange={upd("Guests")} />
              </label>
            </div>

            {formError && <div className="mt-4"><ErrorMessage testid="booking-error" message={formError} /></div>}

            <button type="submit" className="aq-btn-primary mt-6 w-full justify-center" disabled={formBusy} data-testid="booking-submit">
              {formBusy ? <Loader2 size={16} className="animate-spin" /> : null}
              {formBusy ? "Reserving…" : "Reserve My Spot"}
            </button>
            <p className="text-[11px] mt-3" style={{ color: "var(--aq-text-soft)" }}>
              We'll hold your date as soon as you reserve. You can pay your deposit on the next step.
            </p>
          </form>
        )}

        {/* ---- Step B: spot-held panel (paid confirmation handled at page level after Stripe redirect) ---- */}
        {createdReservation && (
          <div data-testid="booking-success-inline">
            <div className="mt-6 p-5 rounded-sm" style={{ background: "rgba(201, 162, 74, 0.10)", border: "1px solid rgba(201,162,74,0.30)" }}>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={20} style={{ color: "var(--aq-gold)" }} />
                <span className="aq-overline" style={{ fontSize: 10 }}>Your spot is held</span>
              </div>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <code data-testid="success-code" className="font-mono text-2xl" style={{ color: "var(--aq-navy)" }}>{code}</code>
                <button type="button" onClick={copyCode} data-testid="success-copy-code" className="text-[12px] underline" style={{ color: "var(--aq-text-soft)" }}>
                  {copied ? "Copied!" : "Copy code"}
                </button>
              </div>
              <p className="text-sm mt-2" style={{ color: "var(--aq-text-soft)" }}>
                We've held this trip for you. Pay your deposit now to confirm the booking — your hold may expire if the deposit isn't completed.
              </p>
            </div>

            <button
              type="button"
              onClick={onPayDeposit}
              disabled={payBusy}
              className="aq-btn-primary mt-5 w-full justify-center"
              data-testid="booking-pay-deposit"
            >
              {payBusy ? <Loader2 size={16} className="animate-spin" /> : null}
              {payBusy ? "Opening Stripe…" : "Pay Deposit Now"}
            </button>

            {payError && <div className="mt-3"><ErrorMessage testid="booking-pay-error" message={payError} /></div>}

            <button
              type="button"
              onClick={onClose}
              data-testid="booking-pay-later"
              title="We'll hold the spot briefly. You can return via Find My Reservation."
              className="mt-3 w-full text-sm underline"
              style={{ color: "var(--aq-text-soft)" }}
            >
              I'll pay later
            </button>
            <p className="text-[11px] mt-3" style={{ color: "var(--aq-text-soft)" }}>
              Until the deposit is paid, this is a hold — not a confirmed booking. You can pay anytime from the "Find My Reservation" panel below using your email + the confirmation code above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessPanel({ status, paymentStatus, reservation }) {
  return (
    <div data-testid="booking-success-panel" className="aq-card p-8" style={{ borderTop: "3px solid var(--aq-gold)" }}>
      <CheckCircle2 size={28} style={{ color: "var(--aq-gold)" }} />
      <h3 className="aq-h2 text-3xl mt-4" style={{ color: "var(--aq-navy)" }}>
        You're booked.
      </h3>
      <p className="mt-3 text-sm" style={{ color: "var(--aq-text-soft)" }}>
        Confirmation code &nbsp;
        <code data-testid="success-code" className="font-mono text-base" style={{ color: "var(--aq-navy)" }}>
          {reservation?.confirmationCode || "—"}
        </code>
      </p>
      <p className="mt-2 text-sm" style={{ color: "var(--aq-text-soft)" }}>
        Payment status: <strong data-testid="success-payment-status">{paymentStatus || status || "pending"}</strong>. We've emailed the captain — pickup details to follow.
      </p>
      <a href="/" className="aq-btn-primary mt-6" data-testid="success-home">Back to home</a>
    </div>
  );
}

function CancelPanel({ reservation }) {
  return (
    <div data-testid="booking-cancel-panel" className="aq-card p-8" style={{ borderTop: "3px solid #A2362B" }}>
      <XCircle size={28} style={{ color: "#A2362B" }} />
      <h3 className="aq-h2 text-3xl mt-4" style={{ color: "var(--aq-navy)" }}>
        Payment cancelled.
      </h3>
      <p className="mt-3 text-sm" style={{ color: "var(--aq-text-soft)" }}>
        Your reservation <code style={{ color: "var(--aq-navy)" }}>{reservation?.confirmationCode}</code> is still held — you can finish payment from the
        "Find My Reservation" panel below, or message the crew.
      </p>
      <a href="#trips" className="aq-btn-primary mt-6" data-testid="cancel-retry">Try again</a>
    </div>
  );
}

export default function Booking() {
  const [rows, setRows] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [activeRow, setActiveRow] = useState(null);     // RA availability row picked from Calendar
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [createdReservation, setCreatedReservation] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Parse Stripe redirect query params on mount
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const paymentFlag = params.get("payment");

  // Phase 2: one-time notification to the backend when Stripe redirects with cancel.
  // sessionStorage prevents a double-call on re-mount; server also dedupes.
  useEffect(() => {
    if (paymentFlag !== "cancelled") return;
    const sessionId = params.get("session_id");
    const reservationId = params.get("reservation_id");
    if (!sessionId) return;
    const key = `aq_cancel_notified_${sessionId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post("/booking/payments/cancelled", {
      sessionId,
      reservationId: reservationId || null,
    }).catch(() => {/* fire-and-forget; server is authoritative on dedup */});
  }, [paymentFlag, params]);

  // Initial availability fetch (next 90 days)
  useEffect(() => {
    const { start, end } = nextNDates(90);
    api.get("/booking/availability", { params: { startDate: start, endDate: end } })
      .then((r) => setRows(r.data))
      .catch((e) => setLoadErr(parseApiError(e)));
  }, []);

  // Poll payment status if landed on success URL
  useEffect(() => {
    if (paymentFlag !== "success") return;
    const sessionId = params.get("session_id");
    const reservationId = params.get("reservation_id");
    if (!sessionId || !reservationId) return;
    let count = 0;
    const max = 20;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.get(`/booking/payments/status/${sessionId}`, {
          params: { reservation_id: reservationId },
        });
        setPaymentStatus(r.data?.status || "pending");
        if (r.data?.status && r.data.status !== "pending") stop = true;
      } catch {/* keep polling */}
    };
    tick();
    const id = setInterval(() => {
      count += 1;
      if (stop || count >= max) { clearInterval(id); return; }
      tick();
    }, 3000);
    return () => clearInterval(id);
  }, [paymentFlag, params]);

  const trips = rows || [];
  // Build a trip-like object for the modal header from the picked row.
  const activeTrip = activeRow ? {
    priceID: activeRow.priceID,
    name: activeRow.priceDisplayName || activeRow.priceName || activeRow.serviceName,
    amount: Number(activeRow.amount || 0),
    validFromTime: activeRow.validFromTime,
    validToTime: activeRow.validToTime,
    assetID: activeRow.assetID,
  } : null;

  const openModalForSlot = (dateISO, row) => {
    setSelectedDate(dateISO);
    setActiveRow(row);
    setSubmitErr(null); setPayErr(null); setCreatedReservation(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSubmitErr(null); setPayErr(null); setCreatedReservation(null);
  };

  // Step A: create reservation only. Never auto-redirect.
  const submitReservation = async (form) => {
    if (!activeRow || !selectedDate) return;
    setSubmitting(true); setSubmitErr(null);
    try {
      const payload = {
        renterEmail: form.Email,
        FirstName: form.FirstName,
        LastName: form.LastName,
        Email: form.Email,
        Phone: form.Phone,
        Guests: Number(form.Guests || 2),
        AssetId: activeRow.assetID,
        // Link the booking to the priced offering so RA computes
        // totalAmount / depositAmount correctly (otherwise reservation lands
        // as a $0 hold and Stripe checkout returns "No deposit is due").
        PriceId: activeRow.priceID,
        ServiceId: activeRow.serviceID,
        StartDateTime: isoStartDateTime(selectedDate, activeRow.validFromTime || "08:00:00"),
        EndDateTime: isoStartDateTime(selectedDate, activeRow.validToTime || "12:00:00"),
        // Phase 2: pass trip metadata to the backend purely so it can render
        // good emails. NOT forwarded to RA.
        tripContext: {
          priceDisplayName: activeRow.priceDisplayName || activeRow.priceName || activeRow.serviceName,
          validFromTime: activeRow.validFromTime,
          validToTime: activeRow.validToTime,
          amount: Number(activeRow.amount || 0),
          assetName: activeRow.assetName || activeRow.displayName,
        },
      };
      const r = await api.post("/booking/reservations", payload);
      setCreatedReservation(r.data);
      setReservation(r.data);
    } catch (e) {
      setSubmitErr(parseApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Step B: only fires when user clicks "Pay Deposit Now" on the success panel.
  const payDeposit = async () => {
    const rid = createdReservation?.reservationId;
    if (!rid) return;
    setPaying(true); setPayErr(null);
    try {
      const checkout = await api.post("/booking/payments/checkout", {
        reservationId: String(rid),
        type: "deposit",
      });
      if (checkout.data?.checkoutUrl) {
        window.location.assign(checkout.data.checkoutUrl);
      } else {
        setPayErr("No Stripe checkout URL returned.");
      }
    } catch (e) {
      setPayErr(parseApiError(e));
    } finally {
      setPaying(false);
    }
  };

  // --- Render ---
  if (paymentFlag === "success") {
    return (
      <section id="trips" data-testid="booking-section" className="aq-section aq-bg-foam">
        <div className="aq-container max-w-3xl">
          <SuccessPanel paymentStatus={paymentStatus} reservation={{ confirmationCode: params.get("reservation_id") }} />
        </div>
      </section>
    );
  }
  if (paymentFlag === "cancelled") {
    return (
      <section id="trips" data-testid="booking-section" className="aq-section aq-bg-foam">
        <div className="aq-container max-w-3xl">
          <CancelPanel reservation={{ confirmationCode: params.get("reservation_id") }} />
        </div>
      </section>
    );
  }

  return (
    <section id="trips" data-testid="booking-section" className="aq-section aq-bg-foam">
      <div className="aq-container">
        <span className="aq-overline">Trips & Booking</span>
        <h2 className="aq-h2 text-4xl sm:text-5xl mt-4" style={{ color: "var(--aq-navy)" }}>
          Choose your day on the water.
        </h2>
        <span className="aq-divider" />
        <p className="max-w-2xl text-[15px] leading-relaxed" style={{ color: "var(--aq-text-soft)" }}>
          Real-time availability — pick a trip, pick a date, hold your slot with a Stripe deposit.
        </p>

        {/* Loading & errors */}
        {rows === null && !loadErr && (
          <div className="mt-10 flex items-center gap-2 text-sm" data-testid="booking-loading" style={{ color: "var(--aq-text-soft)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading availability…
          </div>
        )}
        {loadErr && (
          <div className="mt-10" data-testid="booking-load-error-wrap">
            <ErrorMessage testid="booking-load-error" message={loadErr} />
          </div>
        )}

        {rows && trips.length === 0 && (
          <div className="mt-10 aq-card p-8" data-testid="booking-empty">
            <h3 className="aq-serif text-xl" style={{ color: "var(--aq-navy)" }}>
              No public availability right now.
            </h3>
            <p className="text-sm mt-2" style={{ color: "var(--aq-text-soft)" }}>
              We're between charter windows. Send us a note below and the captain will line up dates for you.
            </p>
            <a href="#contact" className="aq-btn-primary mt-5">Contact the Captain <ArrowRight size={14} /></a>
          </div>
        )}

        {trips.length > 0 && (
          <div className="mt-10" data-testid="booking-calendar-wrap">
            <Calendar rows={rows} onPickSlot={openModalForSlot} />
            <p className="text-xs mt-3" style={{ color: "var(--aq-text-soft)" }}>
              Click any slot to reserve. Stripe handles the deposit — balance is due in person on the day of the trip,
              or via the "Find My Reservation" panel below.
            </p>
          </div>
        )}
      </div>

      <GuestModal
        open={modalOpen}
        trip={activeTrip}
        date={selectedDate}
        onClose={closeModal}
        // Step A
        onSubmitForm={submitReservation}
        formBusy={submitting}
        formError={submitErr}
        // Step B
        createdReservation={createdReservation}
        onPayDeposit={payDeposit}
        payBusy={paying}
        payError={payErr}
      />
    </section>
  );
}
